import PaymentSystemModel from 'Models/PaymentSystemModel';
import { utcDateToMoscowTimezone } from 'Root/helpers/tools';
import GameModel from 'Models/GameModel';
import { EU_COUNTRY_CODES } from 'Models/CountryCodes';
import CurrencyOfferModel from '../models/CurrencyOfferModel';

const GA_ID = 'AW-791125564';
const GA_SKYCOACH_ID = 'AW-16552354901';
const GA_SKYCOACH_ID_2 = 'AW-16594592467';

export default class GoogleTagAnalytics {
  static isDefaultConsentSet = false;

  /**
   * @param {String} action
   * @param {Object} params
   * @param {String} event
   *
   * @returns {boolean}
   */
  static invokeGtag(action, params = null, event = 'event') {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!(window.gtag instanceof Function)) {
      console.warn('GoogleTagAnalytics: gtag function is not defined');
      return false;
    }

    if (!this.isDefaultConsentSet) {
      this.isDefaultConsentSet = true;
      this.setDefaultConsent();
    }

    /**
     * @param {Function} window.gtag
     */
    if (params) {
      window.gtag(event, action, params);
    } else {
      window.gtag(event, action);
    }

    return true;
  }

  static setUserData({ email }) {
    this.invokeGtag('user_data', {
      email,
    }, 'set');
  }

  /**
   * Обновляет статус согласия пользователя на различные сервисы Google
   * @param {Object} params - Параметры для обновления согласия
   * @param {String} params.ad_storage - Статус согласия на хранение данных, связанных с рекламой.
   * Может быть 'granted' или 'denied'.
   * @param {String} params.ad_user_data - Статус согласия на отправку пользовательских данных, связанных с рекламой, в Google.
   * Может быть 'granted' или 'denied'.
   * @param {String} params.ad_personalization - Статус согласия на персонализированную рекламу. Может быть 'granted' или 'denied'.
   * @param {String} params.analytics_storage - Статус согласия на хранение данных, связанных с аналитикой.
   * Может быть 'granted' или 'denied'.
   * @param {Number} params.wait_for_update - Период ожидания команды обновления согласия в миллисекундах.
   * Должно быть положительным целым числом.
   */
  static updateConsent(params) {
    this.invokeGtag('update', params, 'consent');
  }

  /**
   * Устанавливает стандартные значения согласия пользователя на различные сервисы Google.
   * Вызывается при обновлении страницы.
   * Параметр wait_for_update устанавливает время в мс, которое будет дано пользователю на обновление согласия
   */
  static setDefaultConsent() {
    /**
     * SKY-5220: выключаем по-умолчанию согласие на хранение данных, связанных с рекламой в Европе и Великобритании
     * @see https://developers.google.com/tag-platform/security/guides/consent?sjid=13821093586708374303-EU&hl=ru#set_consent_defaults
     *
     */

    // eslint-disable-next-line max-len
    this.invokeGtag('default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 10000,
      region: EU_COUNTRY_CODES,
    }, 'consent');

    this.invokeGtag('default', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 0,
    }, 'consent');
  }

  static fireGtagEvent(action, category, label, value = null) {
    const params = {};

    if (category) {
      params.event_category = category;
    }

    if (label) {
      params.event_label = label;
    }

    if (value) {
      params.value = value;
    }

    if (Object.keys(params).length) {
      return this.invokeGtag(action, params);
    }

    return this.invokeGtag(action);
  }

  static fireUserId(userId) {
    this.pushEventNew({ user_id: String(userId) });
    this.pushEventNew({ userId: String(userId) });
  }

  /**
   * @param {String} gameName
   * @param {Object} order
   * @param {Object} tag
   * @param {Boolean} isFirst
   * @returns {Boolean}
   */
  static firePurchase(order, tag, isFirst = false) {
    let offerName;
    let orderSum;

    const isAccountOrder = order?.product_currency_offer?.marketplace_type === CurrencyOfferModel.TYPES.ACCOUNT;

    if (isAccountOrder) {
      offerName = order.product_currency_offer.outer_name || 'Account order';
      // lower value for account orders because they having less margin
      orderSum = Number((order.sum_in_usd / 3).toFixed(2));
    } else {
      offerName = order.product?.title || 'Custom order';
      orderSum = order.sum_in_usd;
    }

    const gameName = GameModel.ID_TO_DATA[order.game_id].fullTitle;
    const isSent = this.fireGtagEvent('dev_purchase', gameName, offerName, orderSum);

    if (isFirst) {
      this.fireGtagEvent('dev_first_purchase', gameName, offerName, orderSum);

      if (order.positive_coefficient && !isAccountOrder) {
        this.fireGtagEvent('temp_event_test_1st', gameName, offerName, order.positive_coefficient);
      }
    }

    if (isAccountOrder) {
      this.fireGtagEvent('dev_purchase_account', gameName, offerName, order.sum_in_usd);

      if (isFirst) {
        this.fireGtagEvent('dev_first_purchase_account', gameName, offerName, order.sum_in_usd);
      }
    }

    const runs = order.runs || 1;

    this.invokeGtag('purchase', {
      transaction_id: order.id,
      value: order.sum_in_usd,
      currency: 'USD',
      items: [{
        id: order.product?.id || 'Custom order',
        name: offerName,
        brand: gameName,
        category: tag.title,
        quantity: runs,
        price: order.sum_in_usd / runs,
        variant: utcDateToMoscowTimezone(order.created_at),
        google_business_vertical: 'custom',
      }],
    });

    if (order.positive_coefficient && !isAccountOrder) {
      this.invokeGtag('temp_event_test', {
        transaction_id: order.id,
        value: order.positive_coefficient,
        currency: 'USD',
        items: [{
          id: order.product?.id || 'Custom order',
          name: offerName,
          brand: gameName,
          category: tag.title,
          quantity: runs,
          price: order.sum_in_usd / runs,
          variant: utcDateToMoscowTimezone(order.created_at),
          google_business_vertical: 'custom',
        }],
      });
    }

    return isSent;
  }

  /**
   * @param {Object} product
   * @param {Object} tag
   * @param {Number} price
   * @returns {Boolean}
   */
  static firePageView(product, tag, price) {
    const gameName = GameModel.ID_TO_DATA[product.game].fullTitle;

    const items = [{
      id: product.id,
      brand: gameName,
      name: product.title,
      category: tag.title,
      google_business_vertical: 'custom',
    }];

    /* Пока что дублируется функционал, в будущем могут отказаться от page_view */
    this.invokeGtag('page_view', {
      send_to: GA_ID,
      value: price,
      items,
    });

    this.invokeGtag('page_view', {
      send_to: GA_SKYCOACH_ID,
      value: price,
      items,
    });

    this.invokeGtag('page_view', {
      send_to: GA_SKYCOACH_ID_2,
      value: price,
      items,
    });

    this.invokeGtag('view_item', {
      value: price,
      items,
    });

    return true;
  }

  /**
   * @param {String} buttonAction
   * @param {String} offerName
   * @param {String} category
   * @returns {Boolean}
   */
  static fireBuyClick(buttonAction, offerName, category = 'button_click') {
    return this.fireGtagEvent(buttonAction, category, offerName);
  }

  /**
   * @param {String} buttonAction
   * @param {String} label
   * @returns {Boolean}
   */
  static fireError(buttonAction, label) {
    return this.fireGtagEvent(buttonAction, 'error', label);
  }

  /**
   * @param {Object} product
   * @param {String} tagTitle
   * @param {Number} price
   * @returns {Boolean}
   */
  static firePaymentPopup(product, tagTitle, price) {
    const gameName = GameModel.ID_TO_DATA[product.game].fullTitle;

    this.fireGtagEvent('chose_payment_type_2', 'pop_up', product.title);

    this.invokeGtag('add_to_cart', {
      items: [{
        id: product?.id || 'Custom order',
        category: tagTitle,
        brand: gameName,
        name: product.title,
        price,
        google_business_vertical: 'custom',
      }],
    });

    return true;
  }

  static emitAltcraftPopup(action, label) {
    this.fireGtagEvent(action, 'pop up', label);
  }

  static emitXurPopup(label) {
    this.fireGtagEvent('subscribed', 'xur', label);
  }

  static fireTipsEvent(label) {
    this.fireGtagEvent('leave_tips_click', 'tips', label);
  }

  static fireProRegisterEvent() {
    this.fireGtagEvent('finish', 'pro_register');
  }

  static fireRepeatPurchaseEvent() {
    this.fireGtagEvent('click', 'repeat_button');
  }

  static fireSavedOrdersEvent(action) {
    this.fireGtagEvent(action, 'saved_orders');
  }

  // ________________________ СТАРАЯ АНАЛИТИКА ________________________

  static pushEventNew(params) {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!window.dataLayer) {
      // eslint-disable-next-line no-console
      console.warn('GoogleTagAnalytics: dataLayer is not defined');
      return false;
    }

    window.dataLayer.push(params);
    return true;
  }

  static pushEvent(category, action, label = null, value = null, additionalParams = {}) {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.dataLayer) {
      // eslint-disable-next-line no-console
      console.warn('GoogleTagAnalytics: dataLayer is not defined');
      return;
    }

    let params = {
      event: 'autoEvent',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    };
    params = Object.assign(params, additionalParams);

    window.dataLayer.push(params);
  }

  static emitHomePage() {
    return this.pushEventNew({
      pageType: 'home',
    });
  }

  static emitProductNavigationNew(gameTitle, product, price, currency) {
    this.pushEventNew({ ecommerce: null });
    return this.pushEventNew({
      event: 'productClick',
      ecommerce: {
        currencyCode: currency,
        click: {
          products: [{
            name: product.title,
            id: product.id,
            price,
            category: gameTitle,
          }],
        },
      },
    });
  }

  static emitProductDetailNew(gameTitle, product, price, currency) {
    this.pushEventNew({ ecommerce: null });
    return this.pushEventNew({
      ecommerce: {
        currencyCode: currency,
        detail: {
          products: [{
            name: product.title,
            id: product.id,
            price,
            category: gameTitle,
          }],
        },
      },
      pageType: 'product',
    });
  }

  static emitOpenPaymentNew(gameTitle, product, price, currency, runs) {
    this.pushEventNew({ ecommerce: null });
    return this.pushEventNew({
      event: 'checkout',
      ecommerce: {
        currencyCode: currency,
        checkout: {
          actionField: { step: 1 },
          products: [{
            name: product.title,
            id: product.id,
            price,
            category: gameTitle,
            quantity: runs,
          }],
        },
      },
      pageType: 'checkout',
    });
  }

  static emitBuyForNew(paymentSystemTitle) {
    this.pushEventNew({ ecommerce: null });
    return this.pushEventNew({
      event: 'checkoutOption',
      ecommerce: {
        checkout_option: {
          actionField: { step: 2, option: paymentSystemTitle },
        },
      },
    });
  }

  static emitPayNew(gameTitle, product, order, currency, promocodeTitle, isFirstPayment) {
    this.pushEventNew({ ecommerce: null });
    const isSent = this.pushEventNew({
      ecommerce: {
        currencyCode: currency,
        purchase: {
          actionField: {
            id: order.id,
            revenue: order.sum,
            coupon: promocodeTitle,
          },
          products: [{
            name: product.title,
            id: product.id,
            price: order.sum,
            category: gameTitle,
            quantity: 1,
          }],
        },
      },
      pageType: 'purchase',
    });

    if (isFirstPayment) {
      this.pushEventNew({ event: 'firstTimePurchase' });
      this.pushEventNew({
        ecommerce: {
          currencyCode: currency,
          purchase: {
            actionField: {
              id: order.id,
              revenue: order.sum,
              coupon: promocodeTitle,
            },
            products: [{
              name: product.title,
              id: product.id,
              price: order.sum,
              category: gameTitle,
              quantity: 1,
            }],
          },
        },
        pageType: 'firstTimePurchase',
      });
    }

    return isSent;
  }

  static emitProductListNew(gameTitle, products, region) {
    if (!products.length) {
      return false;
    }

    const impressions = products.map((product) => {
      let price = null;

      if (product.initial_prices && region?.shortTitle) {
        const regionKey = region.shortTitle.toLowerCase();
        price = product.initial_prices[regionKey];
      }

      return {
        name: product.title,
        id: product.id,
        price,
        category: gameTitle,
      };
    });

    this.pushEventNew({ ecommerce: null });
    return this.pushEventNew({
      ecommerce: {
        currencyCode: 'EUR',
        impressions,
      },
      pageType: 'category',
    });
  }

  static emitPageViewEvent(title, url, host) {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.dataLayer) {
      // eslint-disable-next-line no-console
      console.warn('GoogleTagAnalytics: dataLayer is not defined');
      return;
    }

    window.dataLayer.push({
      event: 'virtualPageview',
      virtualTitle: title,
      virtualUrl: url,
      virtualHost: host,
    });
  }

  static emitChooseProductCategory(category) {
    this.pushEvent('Product category', 'Choose', category);
  }

  static emitSearchActivated() {
    this.pushEvent('Search', 'Activated');
  }

  static emitSearchInput3symbols() {
    this.pushEvent('Search', 'Input', '3 symbols');
  }

  static emitSearchShowAll() {
    this.pushEvent('Search', 'Show All');
  }

  static emitOpenItem(from) {
    this.pushEvent('Item', 'Open', from);
  }

  static emitStartOwnOrder() {
    this.pushEvent('Own order', 'Start');
  }

  static emitChooseItemOption(option) {
    this.pushEvent('Item', 'Choose item options', option);
  }

  static emitChoosePaymentOption(paymentSystemId) {
    const paymentSystem = paymentSystemId === PaymentSystemModel.PAYPAL.id ? 'PayPal' : 'Credit card';
    this.pushEvent('Item', 'Choose payment options', paymentSystem);
  }

  static emitBuyItem(success = true) {
    const result = success ? 'Success' : 'Error';
    this.pushEvent('Item', 'Buy now', result);
  }

  static emitPaidSuccess(sum = null, gameName = null) {
    const additionalParams = gameName ? { gameName } : {};
    this.pushEvent('Order', 'Paid success', null, sum, additionalParams);
  }

  static emitFirstPayment() {
    this.pushEvent('Order', 'Order success', 'First payment');
  }

  static emitGoToMessenger(messenger) {
    this.pushEvent('Order', 'Go to messenger', messenger);
  }

  static emitSelectRegion(region) {
    this.pushEvent('Region', 'Select', region);
  }

  static emitChangeCatalogOrder(order) {
    this.pushEvent('Catalog', 'Change order', order);
  }

  static emitDepositInit() {
    this.pushEvent('Deposit', 'Init');
  }

  static emitOrderCreateOrderError() {
    this.pushEvent('Order', 'Create order', 'Error');
  }

  static emitOrderSelectOptions(option) {
    this.pushEvent('Order', 'Select options', option);
  }

  static emitItemOpenChatEvent() {
    this.pushEvent('Item', 'OpenChat');
  }

  // static emitLastHopePopupOpen() {
  //   this.pushEvent('popup', 'show', 'first_order_discount');
  // }
  //
  // static emitLastHopePopupChat() {
  //   this.pushEvent('popup', 'start_chat', 'first_order_discount');
  // }

  static emitChatOpenChat() {
    this.pushEvent('Chat', 'OpenChat');
  }
}
